import paper from 'paper';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';

import NS from '@core/app/constants/namespaces';

import {
  applyHoles,
  buildDecorations,
  buildSvgView,
  generateCustomBaseShape,
  importBasePath,
  loadShape,
} from './builders';
import {
  getCategoryById,
  getDefaultCategory,
  getDefaultState,
  getStateForCategory,
  resolveCategory,
} from './categories';
import type { KeychainViewMode } from './constants';
import { PX_TO_MM_RATIO } from './constants';
import type { KeyChainCategory, KeyChainShape, KeyChainState } from './types';

interface StoreState {
  // Paper.js cache (runtime-only, not serializable)
  basePath: null | paper.PathItem;
  // Counter incremented at the start of every async buildBaseShape call
  // — used to bail out of stale builds.
  buildVersion: number;
  // Resolved category (with active variant's svgContent/options merged in)
  category: KeyChainCategory;
  // Paper.js cache for inner path, path for other layer decoration elements (e.g. text-body glyphs)
  innerPath: null | paper.PathItem;
  isModified: boolean;
  project: null | paper.Project;
  resultPath: null | paper.PathItem;
  // Computed result consumed by Preview and exportToCanvas
  shape: KeyChainShape | null;
  // Scale ratio derived from target size vs natural bounds
  sizeRatio: number;
  // Keychain parameters (initialized on mount)
  state: KeyChainState;
  // Preview view mode — controls which cached SVG the Preview shows
  viewMode: KeychainViewMode;
}

const initialState: StoreState = {
  basePath: null,
  buildVersion: 0,
  category: getDefaultCategory(),
  innerPath: null,
  isModified: false,
  project: null,
  resultPath: null,
  shape: null,
  sizeRatio: 1,
  state: null as unknown as KeyChainState, // Initialized in setCategoryState called from useEffect on mount
  viewMode: 'design',
};

const createEmptyShape = (project: null | paper.Project): KeyChainShape => {
  const emptySvg = document.createElementNS(NS.SVG, 'svg');

  emptySvg.setAttribute('xmlns', NS.SVG);

  const emptyPath = new paper.Path();

  return {
    bounds: project?.activeLayer.bounds ?? new paper.Rectangle(0, 0, 0, 0),
    decorations: { emboss: [], engraving: [], refPaths: [] },
    designSvg: emptySvg,
    explodedSvg: emptySvg.cloneNode(true) as SVGSVGElement,
    innerPath: null,
    resultBasePath: emptyPath,
  };
};

const generateBaseShape = (category: KeyChainCategory): { basePath: null | paper.PathItem; project: paper.Project } => {
  const project = new paper.Project(document.createElement('canvas'));
  const { id, svgContent } = category;

  if (!svgContent) {
    return { basePath: null, project };
  }

  const basePath = importBasePath(project, svgContent);

  if (!basePath) {
    console.error('Failed to import base path for category', id);

    return { basePath: null, project };
  }

  basePath.reorient(false, true); // Force clockwise orientation for consistent normal calculations

  return { basePath, project };
};

const calculateSize = (targetSize: KeyChainState['size'], basePath: null | paper.PathItem, sizeRatio?: number) => {
  const { dimension, value } = targetSize;

  if (!basePath || value <= 0 || basePath.bounds[dimension] <= 0) return null;

  sizeRatio = sizeRatio ?? (value * PX_TO_MM_RATIO) / basePath.bounds[dimension];

  return { sizeRatio };
};

const useKeychainShapeStore = create(
  combine(initialState, (set, get) => ({
    /**
     * Composes the final shape by cloning the cached basePath and applying holes, texts, and
     * element shapes from the current state. Pure function over the store snapshot — synchronous.
     *
     * The previous shape's owned paper objects (resultBasePath, innerPath) are disposed
     * before installing the new shape.
     */
    applyOptions: async (): Promise<KeyChainShape> => {
      const { basePath, category, innerPath, project, shape: oldShape, sizeRatio, state } = get();
      const { defaultViewBox, options } = category;

      // Dispose previous shape's owned paper objects
      oldShape?.resultBasePath.remove();
      oldShape?.innerPath?.remove();

      if (!basePath || !project) {
        const emptyShape = createEmptyShape(project);

        set({ shape: emptyShape, sizeRatio: 1 });

        return emptyShape;
      }

      // Apply all hole boolean ops on a clone of the cached base path
      const holeDefs = options.holes ?? [];
      const resultBasePath = applyHoles(basePath.clone(), state, holeDefs, sizeRatio);
      const bounds = resultBasePath.bounds;

      // Build decoration nodes (text + element shapes + decoration paths)
      const texts = options.texts ?? [];
      const elements = options.elements ?? [];
      const decorationPaths = options.decorationPaths ?? [];
      const decorations = await buildDecorations(project, state, texts, elements, decorationPaths);
      // Clone the cached inner path so the canonical inner path is never mutated
      const innerPathClone = innerPath ? innerPath.clone() : null;

      const buildParams = {
        bounds,
        decorations,
        defaultViewBox,
        innerPath: innerPathClone,
        resultBasePath,
      };
      const designSvg = buildSvgView('design', buildParams);
      const explodedSvg = buildSvgView('exploded', buildParams);

      const shape: KeyChainShape = {
        bounds,
        decorations,
        designSvg,
        explodedSvg,
        innerPath: innerPathClone,
        resultBasePath,
      };

      set({ shape });

      return shape;
    },

    /**
     * Builds (or rebuilds) the cached base path for the given category.
     * - For non-text categories, skips work when the cached basePath already matches the category id.
     * - For the text category, always rebuilds because the path is derived from user-typed text.
     *
     * Returns `true` if the caller should proceed with `applyOptions()`, or `false` when the call
     * was superseded by a newer one (stale-bail).
     */
    buildBaseShape: async (category: KeyChainCategory): Promise<boolean> => {
      const {
        basePath: cachedBasePath,
        buildVersion: oldVersion,
        category: cachedCategory,
        project: cachedProject,
        state,
      } = get();

      const { isCustomShape } = category;
      const { size } = state;

      // Cache hit: same resolved category object with valid paper objects → recompute sizeRatio only
      if (!isCustomShape && category === cachedCategory && cachedProject && cachedBasePath) {
        const sizeResult = calculateSize(size, cachedBasePath);

        if (sizeResult) set(sizeResult);

        return true;
      }

      // Bump the build version so any in-flight async build can detect that it's stale
      const buildVersion = oldVersion + 1;

      set({ buildVersion });

      if (isCustomShape) {
        const customShapeTextOption = category.options.customShapeText;

        if (!customShapeTextOption) {
          console.error('No custom shape text option found in category', category.id);
          cachedBasePath?.remove();
          get().innerPath?.remove();
          cachedProject?.remove();
          set({
            basePath: null,
            category,
            innerPath: null,
            project: new paper.Project(document.createElement('canvas')),
          });

          return true;
        }

        const { customShapeElement, customShapeText, outlineOffset } = state;
        const customShapeElementOption = category.options.customShapeElement;

        if (customShapeElement?.enabled && customShapeElement.shapeKey) {
          await loadShape(customShapeElement.shapeKey);
        }

        const result = await generateCustomBaseShape(
          customShapeElementOption,
          customShapeText,
          customShapeElement,
          outlineOffset,
          state.size,
        );

        // Stale: another buildBaseShape call has started since this one — discard our result
        if (get().buildVersion !== buildVersion) {
          result.basePath?.remove();
          result.innerPath?.remove();
          result.project.remove();

          return false;
        }

        // Winner: clean up whatever is currently cached
        const { basePath: prevBasePath, innerPath: prevInnerPath, project: prevProject } = get();

        prevBasePath?.remove();
        prevInnerPath?.remove();
        prevProject?.remove();

        set({
          basePath: result.basePath,
          category,
          innerPath: result.innerPath,
          project: result.project,
        });

        const sizeResult = calculateSize(size, result.basePath, result.sizeRatio);

        if (sizeResult) set(sizeResult);

        return true;
      }

      // Non-custom base path is fully synchronous — no race possible
      const result = generateBaseShape(category);

      cachedBasePath?.remove();
      get().innerPath?.remove();
      cachedProject?.remove();

      set({
        basePath: result.basePath,
        category,
        innerPath: null,
        project: result.project,
      });

      const sizeResult = calculateSize(size, result.basePath);

      if (sizeResult) set(sizeResult);

      return true;
    },

    reset: () => {
      const { basePath, innerPath, project, shape } = get();

      basePath?.remove();
      innerPath?.remove();
      shape?.resultBasePath.remove();
      shape?.innerPath?.remove();
      project?.remove();
      set({ ...initialState, state: getDefaultState() });
    },

    setCategoryState: (category: KeyChainCategory) => {
      // Drop cached paper objects so the next buildBaseShape call rebuilds for the new category
      const { basePath, innerPath, project, shape } = get();

      basePath?.remove();
      innerPath?.remove();
      shape?.resultBasePath.remove();
      shape?.innerPath?.remove();
      project?.remove();
      set({
        basePath: null,
        category: resolveCategory(category),
        innerPath: null,
        isModified: false,
        project: null,
        shape: null,
        state: getStateForCategory(category),
      });
    },

    setVariant: (key: string) => {
      const { basePath, innerPath, project, shape, state } = get();
      const resolved = resolveCategory(getCategoryById(state.categoryId), key);

      // Keep the ratio; recompute the concrete size against the new variant's default size,
      // with the dimension pinned to the new variant's default dimension.
      const { dimension, value: defaultValue } = resolved.defaultSize;
      const size = { dimension, value: (defaultValue * state.ratio) / 100 };

      // Clear cached paper objects so buildBaseShape rebuilds for the new variant
      basePath?.remove();
      innerPath?.remove();
      shape?.resultBasePath.remove();
      shape?.innerPath?.remove();
      project?.remove();
      set({
        basePath: null,
        category: resolved,
        innerPath: null,
        project: null,
        shape: null,
        state: { ...state, size, variantKey: key },
      });
    },

    setViewMode: (viewMode: KeychainViewMode) => {
      set({ viewMode });
    },

    updateState: (updates: Partial<KeyChainState>) => {
      set((prev) => ({ isModified: true, state: { ...prev.state, ...updates } }));
    },
  })),
);

export default useKeychainShapeStore;
